import type { ArgsOf, RequestOf } from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";

import { commonAllAccess } from "./all.ts";
import { commonGroupAccess, commonSshAccess } from "./group.ts";
import {
  aliasParameter,
  groupNameParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";

const parentLabel = "Azure subscription";

export const AzureSshAccesses = {
  session: Access({
    ...commonSshAccess,
    maturity: "ga",
    delegation: {},
    generated: {
      directoryId: { type: "string" },
      // This is a generated parameter because it can take up to 10 seconds to
      // iterate through a directory and find the matching user. We don't want
      // to slow down the request creation process.
      principalId: { type: "string" },
      linuxUserName: { type: "string" },
      bastionHostRoleAssignmentId: { type: "string" },
      networkInterfaceRoleAssignmentId: { type: "string" },
      virtualMachineRoleAssignmentId: { type: "string" },
      jumpHostRoleAssignmentId: { type: "string" },
    },
    parameters: {
      provider: { ...requiredProviderParameter, choices: ["azure"] as const },
      destination: sessionDestinationParameter,
      parent: parentParameter(parentLabel),
      alias: aliasParameter,
      group: groupNameParameter,
      sudo: sudoParameter,
      region: regionParameter,
      publicKey: publicKeyParameter,
      zone: zoneParameter,
      // Exactly one of `bastionHost` / `jumpHost` is set, depending on how the
      // subscription's bastion-host installation is configured. Grouping the
      // per-host fields keeps room for additional properties (e.g. publicIp).
      bastionHost: {
        type: "object",
        label: "Bastion host",
        optional: true,
        properties: {
          id: {
            type: "generated",
            label: "Bastion host ID",
          },
          roleId: {
            type: "generated",
            label: "Bastion host role definition ID",
          },
        },
      },
      jumpHost: {
        type: "object",
        label: "Jump host",
        optional: true,
        properties: {
          id: {
            type: "generated",
            label: "Jump host VM resource ID",
          },
          roleId: {
            type: "generated",
            label: "Jump host role definition ID",
          },
          publicIp: {
            type: "generated",
            label: "Jump host public IP address",
          },
        },
      },
      accessRoleId: {
        type: "generated",
        label: "Role definition ID",
      },
      resource: {
        type: "object",
        label: "Data",
        properties: {
          instanceName: {
            type: "generated",
            label: "Instance name",
          },
          instanceId: {
            type: "generated",
            label: "Resource ID",
          },
          subscriptionId: {
            type: "generated",
            label: "Subscription ID",
          },
          subscriptionName: {
            type: "generated",
            label: "Subscription name",
          },
          resourceGroupId: {
            type: "generated",
            label: "Resource group ID",
          },
          groupTag: {
            type: "object",
            label: "Group tag",
            optional: true,
            properties: {
              key: {
                type: "generated",
                label: "Group tag key",
              },
              value: {
                type: "generated",
                label: "Group tag value",
              },
            },
          },
          region: {
            type: "generated",
            label: "Region",
          },
          networkInterface: {
            type: "object",
            label: "Network interface",
            properties: {
              id: {
                type: "generated",
                label: "Network interface ID",
              },
              subnetId: {
                type: "generated",
                label: "Subnet ID",
              },
              // The target's private IP, used to hop to it through a jump host.
              privateIp: {
                type: "generated",
                label: "Private IP address",
                optional: true,
              },
            },
          },
        },
      },
    },
    render: {
      locator: "parent",
      resource: "destination",
      policy: "sudo",
    } as const,
  }),
  group: commonGroupAccess(parentLabel),
  all: commonAllAccess,
  parent: commonParentAccess(parentLabel),
} satisfies EmptyAccesses;

export type AzureSshAccesses = typeof AzureSshAccesses;
export type AzureSshAccess = AzureSshAccesses[keyof AzureSshAccesses];
export type AzureSshAccessArgs = ArgsOf<AzureSshAccesses>;

export type AzureSshSessionAccess = AzureSshAccesses["session"];
export type AzureSshSessionAccessArgs = ArgsOf<AzureSshSessionAccess>;
export type AzureSshSessionRequest = RequestOf<AzureSshSessionAccess>;

export type AzureSshGroupAccess = AzureSshAccesses["group"];
export type AzureSshGroupAccessArgs = ArgsOf<AzureSshGroupAccess>;

export type AzureSshParentAccess = AzureSshAccesses["parent"];
export type AzureSshParentAccessArgs = ArgsOf<AzureSshParentAccess>;

export type AzureSshAllAccess = AzureSshAccesses["all"];
export type AzureSshAllAccessArgs = ArgsOf<AzureSshAllAccess>;
